import { getAuth, RecaptchaVerifier, signInWithPhoneNumber, type ConfirmationResult } from 'firebase/auth';
import { getFirebaseApp } from './config';
import { isNativeApp } from '../platform';
import { sendPhoneOtpNative, verifyPhoneOtpNative } from './nativeAuth';

export function formatFirebasePhoneAuthError(err: unknown): string {
  const code = typeof err === 'object' && err && 'code' in err ? String((err as { code?: string }).code) : '';
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'this site';

  switch (code) {
    case 'auth/captcha-check-failed':
      return `Phone verification is blocked for ${hostname}. In Firebase Console → Authentication → Settings → Authorized domains, add "${hostname}" (and cuet.webfoxx.com if you use a custom domain). Then retry.`;
    case 'auth/unauthorized-domain':
      return `This domain (${hostname}) is not authorized in Firebase. Add it under Authentication → Settings → Authorized domains.`;
    case 'auth/invalid-phone-number':
      return 'Enter a valid phone number with country code (e.g. +919876543210).';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a few minutes and try again.';
    case 'auth/quota-exceeded':
      return 'SMS quota exceeded. Upgrade Firebase to the Blaze plan and enable Phone sign-in.';
    default:
      break;
  }

  if (err instanceof Error && err.message) {
    return err.message;
  }

  return 'Failed to send OTP. Please try again.';
}

export function formatPhoneNumber(raw: string) {
  const trimmed = raw.trim();
  if (trimmed.startsWith('+')) return trimmed;
  if (trimmed.length === 10) return `+91${trimmed}`;
  throw new Error('Please specify country code (e.g. +919999999999)');
}

export async function startPhoneSignIn(phoneNumber: string): Promise<{ mode: 'web'; confirmation: ConfirmationResult } | { mode: 'native'; verificationId: string }> {
  const formatted = formatPhoneNumber(phoneNumber);

  if (isNativeApp()) {
    const verificationId = await sendPhoneOtpNative(formatted);
    return { mode: 'native', verificationId };
  }

  const app = getFirebaseApp();
  if (!app) throw new Error('Firebase is not configured on the client.');
  const auth = getAuth(app);
  if (process.env.NODE_ENV === 'development') {
    auth.settings.appVerificationDisabledForTesting = true;
  }

  const container = document.getElementById('recaptcha-container');
  if (container) container.innerHTML = '';

  const recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
  try {
    const confirmation = await signInWithPhoneNumber(auth, formatted, recaptchaVerifier);
    return { mode: 'web', confirmation };
  } catch (err) {
    recaptchaVerifier.clear();
    throw new Error(formatFirebasePhoneAuthError(err));
  }
}

export async function completePhoneSignIn(
  state: { mode: 'web'; confirmation: ConfirmationResult } | { mode: 'native'; verificationId: string },
  otpCode: string,
): Promise<string> {
  if (state.mode === 'native') {
    return verifyPhoneOtpNative(state.verificationId, otpCode);
  }

  const result = await state.confirmation.confirm(otpCode);
  return result.user.getIdToken();
}
